import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  AtsCandidatePullMode,
  AtsSettingsDocument,
  SzMappedStageType,
  useSetAtsCandidatePullModeMutation,
  useSetAtsStageMapMutation,
  useSetAtsStagePushEnabledMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  EditFormWrapper,
  FieldLabel,
  SelectFormField,
  TextArea,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { SwitchRow } from './SwitchRow';
import type { TSelectOption } from '@spotted-zebra-uk/ui-components';
import {
  AtsSettings,
  findDuplicateStageRef,
  findStageMapCompletionClash,
  onOff,
  parseStepIds,
  PULL_MODE_LABELS,
  StageMapEntry,
  StageMapFormValues,
  stageMapSummary,
  StageOption,
  SZ_STAGE_TYPE_LABELS,
  SZ_STAGE_TYPES,
  toFormStageMap,
  toStageMapEntries,
  WORKDAY_TOOL,
} from './IntegrationsTab.helpers';
import styles from './IntegrationsTab.module.scss';

const dataTestId = 'integrations-stage-mapping-form';

const PULL_MODE_CAPTION =
  'When applicants on a linked role are created in Spotted Zebra. ' +
  '“All applicants” pulls every synced applicant. “Only on entering a ' +
  'mapped stage” creates a candidate the first time their application ' +
  'reaches a stage in the map above — applicants outside mapped stages ' +
  'stay out of Spotted Zebra. Declined and hired applications are never ' +
  'pulled.';

const STAGE_PUSH_CAPTION =
  'When enabled, a candidate progressing in Spotted Zebra into a mapped ' +
  'stage type also moves their ATS application to the mapped ATS stage. ' +
  'Outbound only — stage moves made in the ATS always sync in regardless.';

type StageMappingCardProps = {
  companyId: number;
  atsIdentifier: string | null | undefined;
  atsSettings: AtsSettings;
  stageOptions: StageOption[];
  stagesLoading: boolean;
  missingRefCount: number;
};

export function StageMappingCard(props: StageMappingCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  return isEditing ? (
    <StageMappingForm
      {...props}
      onCancelClick={() => setIsEditing(false)}
      onSave={() => setIsEditing(false)}
    />
  ) : (
    <StageMappingView {...props} onEditClick={() => setIsEditing(true)} />
  );
}

function StageMappingView({
  atsSettings,
  stageOptions,
  onEditClick,
}: StageMappingCardProps & { onEditClick: () => void }) {
  const stageNamesByRef = new Map(
    stageOptions.map(option => [option.value, option.label])
  );
  const entriesByType = new Map<SzMappedStageType, StageMapEntry>(
    atsSettings.stageMap.map(entry => [entry.szStageType, entry])
  );

  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Stage mapping</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        {SZ_STAGE_TYPES.map(szStageType => (
          <InfoItem
            key={szStageType}
            label={SZ_STAGE_TYPE_LABELS[szStageType]}
            value={stageMapSummary(
              entriesByType.get(szStageType),
              stageNamesByRef
            )}
          />
        ))}
        <InfoItem
          label="Pull candidates into SZ"
          value={PULL_MODE_LABELS[atsSettings.candidatePullMode]}
        />
        <InfoItem
          label="Progression write-back"
          value={onOff(atsSettings.stagePushEnabled)}
        />
      </div>
    </div>
  );
}

/**
 * Step-id input for Workday rows: one id per line, since a stage type can
 * need one per process variant — two for some tenants, hundreds for
 * others. A pasted list is split, trimmed and de-duplicated when the field
 * loses focus; the count reflects the ids that will be saved. Labelled the
 * same way as the results card, because the design system's Formik field
 * floats its label only once the field has a value.
 */
function StepIdsInput({ name }: { name: string }) {
  const [field, , helpers] = useField<string>(name);
  const count = parseStepIds(field.value).length;
  return (
    <FieldLabel id={name} label="Workday step IDs">
      <TextArea
        id={name}
        value={field.value}
        ariaLabel="Workday step IDs"
        rows={2}
        autosize
        onChange={value => helpers.setValue(value)}
        onBlur={value => {
          helpers.setValue(parseStepIds(value).join('\n'));
          helpers.setTouched(true);
        }}
      />
      <span className={styles.fieldIdCaption}>
        <span>{count === 1 ? '1 step ID' : `${count} step IDs`}</span> · one
        per line; pasted lists are split on commas and spaces
      </span>
    </FieldLabel>
  );
}

type StageMappingFormValues = {
  stageMap: StageMapFormValues;
  candidatePullMode: AtsCandidatePullMode;
  stagePushEnabled: boolean;
};

function StageMappingForm({
  companyId,
  atsIdentifier,
  atsSettings,
  stageOptions,
  stagesLoading,
  missingRefCount,
  onCancelClick,
  onSave,
}: StageMappingCardProps & {
  onCancelClick: () => void;
  onSave: () => void;
}) {
  const { handleMsgType } = useNotification();
  const isWorkday = atsIdentifier === WORKDAY_TOOL;

  const [setAtsStageMapMutation] = useSetAtsStageMapMutation();
  const [setAtsCandidatePullModeMutation] =
    useSetAtsCandidatePullModeMutation();
  const [setAtsStagePushEnabledMutation] = useSetAtsStagePushEnabledMutation();

  const initialStageMap = toFormStageMap(atsSettings);

  const settingsRefetch = {
    refetchQueries: [{ query: AtsSettingsDocument, variables: { companyId } }],
  };

  const handleSave = async (values: StageMappingFormValues) => {
    const blocker =
      findDuplicateStageRef(values.stageMap, isWorkday) ??
      findStageMapCompletionClash(values.stageMap, atsSettings.resultPush);
    if (blocker) {
      handleMsgType({
        type: TNotification.error,
        title: 'Stage mapping could not be saved',
        message: blocker,
        dismissable: true,
      });
      return;
    }

    const stageMapChanged =
      JSON.stringify(values.stageMap) !== JSON.stringify(initialStageMap);
    const pullModeChanged =
      values.candidatePullMode !== atsSettings.candidatePullMode;
    const stagePushChanged =
      values.stagePushEnabled !== atsSettings.stagePushEnabled;

    try {
      // The map mutation goes first, alone: it carries the gateway-side
      // validation, and the failure toast promises nothing was saved.
      // Only once it lands do the two flag mutations run — atomicity
      // across all three would need an API change, but this way a
      // rejected map never leaves the flags changed underneath it.
      if (stageMapChanged) {
        await setAtsStageMapMutation({
          variables: {
            companyId,
            // An empty map clears the configuration entirely.
            entries: toStageMapEntries(values.stageMap, isWorkday),
          },
          ...settingsRefetch,
        });
      }
      await Promise.all([
        pullModeChanged
          ? setAtsCandidatePullModeMutation({
              variables: { companyId, mode: values.candidatePullMode },
              ...settingsRefetch,
            })
          : Promise.resolve(),
        stagePushChanged
          ? setAtsStagePushEnabledMutation({
              variables: { companyId, enabled: values.stagePushEnabled },
              ...settingsRefetch,
            })
          : Promise.resolve(),
      ]);
      handleMsgType({
        type: TNotification.success,
        title: 'Stage mapping updated.',
        dismissable: true,
      });
      onSave();
    } catch {
      handleMsgType({
        type: TNotification.error,
        title: 'Stage mapping could not be updated',
        message: 'Something went wrong. Please try again.',
        dismissable: true,
      });
    }
  };

  const initialValues: StageMappingFormValues = {
    stageMap: initialStageMap,
    candidatePullMode: atsSettings.candidatePullMode,
    stagePushEnabled: atsSettings.stagePushEnabled,
  };

  return (
    <Formik<StageMappingFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm, values, setFieldValue }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Stage mapping"
          formId={dataTestId}
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id={`${dataTestId}-element`}
            className={styles.form}
          >
            <span className={styles.caption}>
              The ATS stage that represents each Spotted Zebra stage. A
              candidate entering a mapped stage is placed into it here; leave a
              row unset to leave that stage unmapped.
            </span>
            {!stagesLoading && stageOptions.length === 0 && (
              <span className={styles.fieldIdCaption}>
                No ATS stages are available yet for this company
                {missingRefCount > 0
                  ? ` (${missingRefCount} cached stage${
                      missingRefCount === 1 ? '' : 's'
                    } have no ATS reference yet — run a full sync first).`
                  : '. Sync jobs first, then configure the mapping.'}
              </span>
            )}
            {SZ_STAGE_TYPES.map(szStageType => {
              const value = values.stageMap[szStageType];
              const selected =
                stageOptions.find(
                  option => option.value === value.atsStageRef
                ) ??
                (value.atsStageRef
                  ? { label: value.atsStageRef, value: value.atsStageRef }
                  : undefined);

              return (
                <div className={styles.resultCard} key={szStageType}>
                  <h4 className={styles.resultCardTitle}>
                    {SZ_STAGE_TYPE_LABELS[szStageType]}
                  </h4>
                  <SelectFormField
                    id={`${dataTestId}__${szStageType}-stage`}
                    label="ATS stage"
                    placeholder={
                      stageOptions.length > 0
                        ? 'Search stages…'
                        : 'No stages available'
                    }
                    isSearchable
                    isDisabled={stageOptions.length === 0}
                    maxMenuHeight={280}
                    options={stageOptions}
                    value={selected}
                    onChange={(option: TSelectOption) =>
                      setFieldValue(
                        `stageMap.${szStageType}.atsStageRef`,
                        option?.value ?? ''
                      )
                    }
                  />
                  {value.atsStageRef !== '' && (
                    <div className={styles.reasonListHeader}>
                      <span className={styles.reasonCount}>
                        Mapped to {value.atsStageRef}
                      </span>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          setFieldValue(
                            `stageMap.${szStageType}.atsStageRef`,
                            ''
                          );
                          setFieldValue(
                            `stageMap.${szStageType}.workdayStepIds`,
                            ''
                          );
                        }}
                        data-testid={`${dataTestId}__clear-${szStageType}`}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                  {isWorkday && value.atsStageRef !== '' && (
                    <StepIdsInput
                      name={`stageMap.${szStageType}.workdayStepIds`}
                    />
                  )}
                </div>
              );
            })}
            <div className={styles.resultCard}>
              <h4 className={styles.resultCardTitle}>Behaviour</h4>
              <SelectFormField
                id={`${dataTestId}__pull-mode`}
                label="Pull candidates into SZ"
                className={styles.settingSelect}
                options={Object.values(AtsCandidatePullMode).map(mode => ({
                  label: PULL_MODE_LABELS[mode],
                  value: mode,
                }))}
                value={{
                  label: PULL_MODE_LABELS[values.candidatePullMode],
                  value: values.candidatePullMode,
                }}
                onChange={(option: TSelectOption) => {
                  if (option?.value) {
                    setFieldValue('candidatePullMode', option.value);
                  }
                }}
              />
              {/* Not the field's bottomText: that slot is a fixed 16px
                  error line and multi-line copy overflows onto the row
                  below. */}
              <span className={styles.fieldIdCaption}>{PULL_MODE_CAPTION}</span>
              <SwitchRow
                id="stagePushEnabled"
                label="Progression write-back"
                caption={STAGE_PUSH_CAPTION}
                value={values.stagePushEnabled}
                switchTestId={`${dataTestId}__stage-push-switch`}
                onToggle={toggled => setFieldValue('stagePushEnabled', toggled)}
              />
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
